import React, { useState } from 'react';
import { Ruleset, Rule, Milestone } from 'types/dt-graphql-apollo-v2';
import {
  Body,
  Button,
  Dropdown,
  Input,
  Menu,
  Overline,
  Title,
  Icon,
  Colors,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { filterRulesetOrRuleBySearch, term } from 'utils/project-ruleset';
import { FeatureFlags, useFeatureFlag } from 'utils/feature-flags';
import { LabelListDisplay, VerticalDivider } from 'components';
import { useAccessControl } from 'hooks/useAccessControl';
import { groupedPermissionsList } from 'store/permissions';
import { JobType } from 'hooks/useStartValidationJob';
import { parseFilterToLogicalRule } from 'utils/rules';
import { RunRulesButton } from './components/RunRulesButton';
import { RuleList } from './components/RuleList';
import { RulesetOptions } from './components/RulesetOptions';
import { ConditionsTreeSummary } from './components/Conditions/ConditionsTreeSummary';
import {
  // MilestoneRuns,
  MilestoneStatusColumn,
} from './components/MilestoneInfo';
import { CopyRulesModal } from './CopyRules/CopyRulesModal';
import { getDisabledRulesCount, isRuleDisabled } from './helpers';

type ViewRulesetProps = {
  schemaCollectionRefId?: string | null;
  ruleset: Ruleset;
  selectedMilestone?: Milestone;
  milestones: Milestone[] | [];
  rules: Rule[];
  onCancel: VoidFunction;
  onCopyRulesSuccess: VoidFunction;
  onEditRuleset: (ruleset: Ruleset) => void;
  onEditRule: (rule: Rule) => void;
  onCreateRule: () => void;
  onDeleteRuleComplete: VoidFunction;
};

export const ViewRuleset = ({
  schemaCollectionRefId,
  ruleset,
  selectedMilestone,
  milestones,
  rules,
  onCancel,
  onCopyRulesSuccess,
  onEditRuleset,
  onEditRule,
  onCreateRule,
  onDeleteRuleComplete,
}: ViewRulesetProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [showImportView, setShowImportView] = useState(false);

  const hasAccess = useAccessControl();
  const canEditRuleset = hasAccess(['Update_Ruleset']);
  const canDeleteRuleset = hasAccess(['Delete_Ruleset']);

  const canRunRules = hasAccess(['Add_Rule_ruleRunStatus']);

  const enabledCopyRules = useFeatureFlag(FeatureFlags.enabledCopyRules);

  // Can create a Rule not connected to a Collection.
  const canCreateIndependentRule =
    hasAccess(groupedPermissionsList.CAN_CREATE_RULE) && !selectedMilestone;
  // Can create a Rule connected to a Collection.
  const canCreateLinkedRule =
    hasAccess(groupedPermissionsList.CAN_CREATE_LINKED_RULE) &&
    selectedMilestone;
  const canCreateRule = canCreateIndependentRule || canCreateLinkedRule;

  const enabledRules = rules.filter((r) => !isRuleDisabled(r));
  const rulesCount = rules.length;
  const disabledRulesCount = getDisabledRulesCount(rules);

  const filteredRulesAfterSearch = filterRulesetOrRuleBySearch(
    search,
    rules
  ) as Rule[];

  const formatedRulesetFiltersTree = parseFilterToLogicalRule(
    ruleset.filters || '{}'
  );

  const handleCopyRefetch = async () => {
    setShowImportView(false);
    onCopyRulesSuccess();
  };

  const renderInfoHeader = () => (
    <>
      <StyledRowHeaderSmall>
        <StyledCol>
          <StyledInlineFlex>
            <Title level={4}>{term.Ruleset}</Title>
            <StyledBodyGroup level={2}>
              <StyledSpanBold>{rulesCount}</StyledSpanBold>{' '}
              {rulesCount !== 1 ? term.rules : term.rule}
              <VerticalDivider />
              <StyledSpanBold>
                {disabledRulesCount}
              </StyledSpanBold> disabled{' '}
              {disabledRulesCount !== 1 ? term.rules : term.rule}
            </StyledBodyGroup>
          </StyledInlineFlex>
        </StyledCol>
        <StyledColButtons>
          {canCreateRule && (
            <Dropdown
              content={
                <Menu>
                  <Menu.Item onClick={onCreateRule}>
                    <div>Rule</div>
                  </Menu.Item>

                  {enabledCopyRules && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        onClick={() => setShowImportView(true)}
                        icon="PanelLeft"
                        iconPlacement="left"
                      >
                        <div>Add existing rules</div>
                      </Menu.Item>
                    </>
                  )}
                </Menu>
              }
            >
              <Button icon="Add" type="primary">
                Create
              </Button>
            </Dropdown>
          )}

          {canEditRuleset && (
            <Button
              icon="Edit"
              type="tertiary"
              aria-label={`Edit ${term.ruleset}`}
              onClick={() => onEditRuleset(ruleset)}
            />
          )}

          <Button
            icon={showInfo ? 'ChevronUp' : 'ChevronDown'}
            type="ghost"
            aria-label="Expand"
            onClick={() => setShowInfo(!showInfo)}
          />
        </StyledColButtons>
      </StyledRowHeaderSmall>
    </>
  );

  const renderInfo = () => (
    <StyledCollapsibleRulesetInfo expanded={showInfo}>
      <StyledGrid>
        <StyledCol>
          <Title level={5}>General info</Title>
          <div>
            <StyledOverline level={1}>NAME</StyledOverline>
            <Body level={2}>{ruleset?.name}</Body>
          </div>
          <div>
            <StyledOverline level={1}>DESCRIPTION</StyledOverline>
            <Body level={2}>{ruleset?.description}</Body>
          </div>
          {/* {selectedMilestone && (
            <MilestoneRuns selectedMilestone={selectedMilestone} />
          )} */}
          <div>
            <StyledOverline level={1}>LABELS</StyledOverline>
            <LabelListDisplay
              labels={ruleset.labels?.map((label) => ({
                text: label,
                key: label,
              }))}
              labelSize="medium"
              limitLabels={5}
            />
          </div>
        </StyledCol>
        <StyledCol>
          <Title level={5}>Content</Title>
          <div>
            <StyledInlineFlex>
              <StyledBodyGroup level={2}>
                <StyledSpanBold>{rulesCount}</StyledSpanBold>{' '}
                {rulesCount !== 1 ? term.rules : term.rule}
              </StyledBodyGroup>
            </StyledInlineFlex>
          </div>
          <div>
            <StyledOverline level={1}>
              {term.SchemaType.toUpperCase()}
            </StyledOverline>
            <Body level={2}>{ruleset?.schemaType}</Body>
          </div>
          <div>
            <StyledOverline level={1}>{term.Ruleset} filters</StyledOverline>
            {ruleset?.filters?.length && (
              <StyledRulesetFilters>
                <ConditionsTreeSummary
                  prefix={`viewruleset-rulesetfilters-${ruleset?.externalId}`}
                  conditionsTree={formatedRulesetFiltersTree}
                />
              </StyledRulesetFilters>
            )}
          </div>
        </StyledCol>
        {selectedMilestone && (
          <StyledMilestoneStatusColumnContainer>
            <MilestoneStatusColumn
              selectedMilestone={selectedMilestone}
              milestones={milestones.filter(
                (milestone) => milestone.externalId !== 'Default condition'
              )}
            />
          </StyledMilestoneStatusColumnContainer>
        )}
      </StyledGrid>
    </StyledCollapsibleRulesetInfo>
  );

  const renderContent = () => (
    <StyledContent>
      <StyledSearchRow>
        <StyledInput
          placeholder={`Search ${term.rule}`}
          icon="Search"
          iconPlacement="left"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </StyledSearchRow>
      <StyledFlexGroup>
        <Title level={5}>{term.Rules}</Title>
        <RuleList
          rules={filteredRulesAfterSearch || []}
          onDeleteRuleComplete={onDeleteRuleComplete}
          onEditRule={onEditRule}
          showPagination
        />
      </StyledFlexGroup>
    </StyledContent>
  );

  return (
    <>
      <StyledRowHeaderTitle>
        <StyledCol>
          <StyledTitleGroup level={4}>
            <StyledBackButton
              icon="ArrowLeft"
              aria-label="Back"
              onClick={onCancel}
              type="ghost"
            />
            {selectedMilestone && (
              <>
                <span>{selectedMilestone?.name}</span>
                <VerticalDivider />
              </>
            )}
            {ruleset.name}
          </StyledTitleGroup>
        </StyledCol>
        <StyledColButtons>
          {canRunRules && (
            <RunRulesButton
              buttonIcon="Play"
              entityId={ruleset.externalId}
              schemaCollectionRefId={schemaCollectionRefId}
              rules={enabledRules}
              type={JobType.RULESET}
            />
          )}

          {(canEditRuleset || canDeleteRuleset) && (
            <RulesetOptions
              ruleset={ruleset}
              onEditRuleset={onEditRuleset}
              onDeleteRulesetComplete={onDeleteRuleComplete}
            />
          )}
        </StyledColButtons>
      </StyledRowHeaderTitle>
      {renderInfoHeader()}
      {renderInfo()}
      {renderContent()}

      <CopyRulesModal
        visible={showImportView}
        destinationMilestone={selectedMilestone}
        destinationRuleset={ruleset}
        milestones={milestones}
        onCancel={() => setShowImportView(false)}
        onCopyRulesSuccess={handleCopyRefetch}
      />
    </>
  );
};

const StyledMilestoneStatusColumnContainer = styled.div`
  > div {
    gap: 1.5rem;
  }
`;

const StyledCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const StyledRowHeader = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  flex-direction: column;
  margin-bottom: 0;
  margin-top: 0;
  width: 100%;
  padding: 2rem 2rem 1.5rem;
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
  background: ${Colors['decorative--grayscale--white']};
`;

const StyledRowHeaderSmall = styled(StyledRowHeader)`
  padding: 1rem 2rem;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;
const StyledRowHeaderTitle = styled(StyledRowHeaderSmall)`
  align-items: center;
  padding-left: 0;
`;

const StyledColButtons = styled(StyledCol)`
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StyledInlineFlex = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 1rem;
`;

const StyledTitleGroup = styled(Title)`
  display: inline-flex;
  gap: 1rem;
  align-items: center;
`;

const StyledBodyGroup = styled(Body)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledSpanBold = styled.span`
  font-weight: 700;
`;

const StyledBackButton = styled(Button)`
  color: var(--cogs-text-primary);
  padding: 0 0 0 0.75rem !important;
  &:hover {
    background: transparent;
  }
`;

const StyledCollapsibleRulesetInfo = styled.div<{
  expanded: boolean;
}>`
  ${({ expanded }) => !expanded && 'overflow: hidden;'};
  max-height: ${({ expanded }) => (expanded ? 'none' : 0)};
  width: 100%;
  margin-bottom: 0;
  margin-top: 0;
  ${StyledCol} {
    gap: 1.5rem;
  }
`;

const StyledGrid = styled.div`
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
  background: ${Colors['decorative--grayscale--white']};
  width: 100%;
  padding: 1rem 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const StyledOverline = styled(Overline)`
  /* Replaced Colors token with CSS var for COGS v9 */
  color: var(--cogs-text-secondary);
  text-transform: uppercase;
`;

const StyledContent = styled.div`
  display: flex;
  flex: 1;
  gap: 1rem;
  flex-direction: column;
  padding: 2rem 2rem 1rem;
`;

const StyledSearchRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
`;

const StyledFlexGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const StyledInput = styled(Input)`
  .cogs-input {
    border-radius: 6px;
  }
  .cogs-icon-Search {
    opacity: 0.25;
    left: 12px;
  }
  .input-clear-button {
    top: calc(50% - 28px / 2);
    right: 2px !important;
    width: 28px;
    height: 28px;
    .cogs-icon.input-content.input-icon.cogs-icon-Close {
      top: calc(50% - 12px / 2);
      width: 12px !important;
      height: 12px !important;
    }
  }
`;

const StyledRulesetFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;
